import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { Prod01 } from '../../constants/ActionType';
import { uploadDocument } from '../../utils/upload';
import BillingRatesPanel from './BillingRatesPanel';
import PlanOfCarePanel from './PlanOfCarePanel';
import { PcmEnrollPrompt } from './ClinicalNote';
import { formatDate } from '../../utils/dateFormat';

// Program billing determination (CCM vs APCM, mutually exclusive per calendar month) + eligibility
// gates (per-program consent, monthly QMB with proof, chronic-condition tier). Gates only FLAG:
// nothing here blocks time capture, data or reporting. Output is a billing OPPORTUNITY, not a claim.

const PROGRAMS = ['CCM', 'PCM', 'RPM', 'APCM'];
const QMB_SOURCES = ['MAC-portal', 'Medicaid-eligibility', 'remittance', 'patient-card', 'upload', 'biller', 'other'];
const CAN_WRITE = ['HrMedical', 'HrLead', 'HrNonMedical', 'doctor', 'admin', 'adminStaff', 'billing'];
const CAN_RATES = ['billing', 'admin'];
const money = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : 'n/a');
const codeStr = (codes) => (codes && codes.length ? codes.map(c => `${c.code}${c.units > 1 ? ` ×${c.units}` : ''}`).join(' + ') : 'none');

const ProgramBillingPanel = ({ patientId, year, month, refreshKey, onEnrollmentChanged }) => {
    const { user } = useSelector(state => state.auth);
    const role = user?.role;
    const canWrite = CAN_WRITE.includes(role);
    const canRates = CAN_RATES.includes(role);
    const [showRates, setShowRates] = useState(false);
    const [visitDate, setVisitDate] = useState('');
    const [visitNote, setVisitNote] = useState('');
    const [oppNote, setOppNote] = useState('');
    const [enrollmentProgram, setEnrollmentProgram] = useState('');
    const [enrollmentDate, setEnrollmentDate] = useState(moment().format('YYYY-MM-DD'));
    const [enrollmentNote, setEnrollmentNote] = useState('');

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);

    const [consentProgram, setConsentProgram] = useState('CCM');
    const [consentMethod, setConsentMethod] = useState('Written');
    const [consentDate, setConsentDate] = useState(moment().format('YYYY-MM-DD'));
    const [consentFile, setConsentFile] = useState(null);
    const [qmbValue, setQmbValue] = useState('true');
    const [qmbSource, setQmbSource] = useState('');
    const [qmbDate, setQmbDate] = useState(moment().format('YYYY-MM-DD'));
    const [qmbNote, setQmbNote] = useState('');
    const [qmbFile, setQmbFile] = useState(null);
    const [chronicInput, setChronicInput] = useState('');
    const [posInput, setPosInput] = useState('');

    const load = useCallback(async () => {
        setError(null);
        try {
            const res = await axios.get(`${Prod01}/billing/determination/${patientId}?year=${year}&month=${month}`);
            setData(res.data);
            setQmbValue(res.data?.qmb?.status === false ? 'false' : 'true');
        } catch (e) { setError(e?.response?.data?.message || e.message); }
    }, [patientId, year, month]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const upload = async (file) => {
        if (!file) return {};
        const up = await uploadDocument(file);
        return { fileName: up.filename, originalFileName: up.originalName || file.name };
    };

    const run = async (fn, okMsg) => {
        setBusy(true); setError(null); setNotice(null);
        try { await fn(); setNotice(okMsg); await load(); } catch (e) { setError(e?.response?.data?.message || e.message); } finally { setBusy(false); }
    };

    const submitConsent = () => run(async () => {
        const f = await upload(consentFile);
        await axios.post(`${Prod01}/billing/consent/${patientId}`, { program: consentProgram, method: consentMethod, consentDate, source: f.fileName ? 'upload' : undefined, ...f });
        setConsentFile(null);
    }, `${consentProgram} consent recorded.`);

    const revokeConsent = (program) => run(async () => {
        await axios.post(`${Prod01}/billing/consent/${patientId}`, { program, status: 'revoked', consentDate: moment().format('YYYY-MM-DD') });
    }, `${program} consent marked revoked.`);

    const submitQmb = (verified) => run(async () => {
        const f = await upload(qmbFile);
        await axios.post(`${Prod01}/billing/qmb/${patientId}`, {
            year, month, qmb: qmbValue === 'true', verified,
            proof: verified ? { source: qmbSource || undefined, evidenceDate: qmbDate || undefined, note: qmbNote || undefined, ...f } : undefined,
        });
        setQmbFile(null); setQmbNote('');
    }, `QMB status for ${moment({ year, month: month - 1 }).format('MMM YYYY')} saved${verified ? ' (verified)' : ' (unverified)'}.`);

    const submitChronic = () => run(async () => {
        const n = chronicInput === '' ? null : Number(chronicInput);
        await axios.post(`${Prod01}/billing/chronic/${patientId}`, { chronicConditionCount: n });
        setChronicInput('');
    }, 'Chronic-condition count saved.');

    const submitVisit = () => run(async () => {
        await axios.post(`${Prod01}/billing/visit/${patientId}`, { initiatingVisitDate: visitDate, note: visitNote || undefined });
        setVisitDate(''); setVisitNote('');
    }, 'Initiating visit saved.');

    const recordOpportunity = (program) => run(async () => {
        await axios.post(`${Prod01}/billing/opportunity/${patientId}`, { year, month, program,
            choosePCM: program === 'PCM' && data?.determination?.pcm?.conflict,
            note: oppNote || undefined });
        setOppNote('');
    }, `${program} opportunity recorded for this month (not a claim).`);

    const submitEnrollment = () => run(async () => {
        if (!enrollmentProgram) throw new Error('Select the program to enroll in.');
        await axios.post(`${Prod01}/billing/enrollment/${patientId}`, {
            program: enrollmentProgram, startedAt: enrollmentDate, note: enrollmentNote.trim(),
            // every program needs qualification + consent + enrollment date; reuse consent already on file
            consent: !data?.consents?.[enrollmentProgram]?.present ? { method: consentMethod, consentDate } : undefined,
            qualifies: true, qualificationBasis: enrollmentNote.trim() || `Qualification for ${enrollmentProgram} confirmed at enrollment`,
        });
        setEnrollmentNote('');
        if (onEnrollmentChanged) await onEnrollmentChanged();
    }, `${enrollmentProgram} enrollment recorded.`);

    const endEnrollment = (id) => run(async () => {
        await axios.post(`${Prod01}/billing/enrollment/${patientId}/${id}/end`, {});
        if (onEnrollmentChanged) await onEnrollmentChanged();
    }, 'Enrollment episode ended.');

    const submitPos = () => run(async () => {
        await axios.post(`${Prod01}/billing/pos/${patientId}`, { placeOfService: posInput });
        setPosInput('');
    }, 'Place of service saved.');

    const voidOpportunity = (id) => run(async () => {
        await axios.post(`${Prod01}/billing/opportunity/${patientId}/${id}/void`, { reason: 'voided from portal' });
    }, 'Opportunity voided.');

    if (error && !data) return <div className="alert alert-danger py-2 mt-2">{error}</div>;
    if (!data) return null;

    const det = data.determination || {};
    const blocks = (det.flags || []).filter(f => f.severity === 'block');
    const warnings = (det.flags || []).filter(f => f.severity === 'warning');
    const infos = (det.flags || []).filter(f => f.severity === 'info');
    const visit = data.initiatingVisit || {};
    const recorded = data.recordedOpportunities || [];
    const rateScopes = det.rates?.byCode || {};
    const scopeOf = (code) => rateScopes[code]?.scope;
    const alreadyRecorded = (p) => recorded.some(r => r.program === p);
    const monthLabel = moment({ year, month: month - 1 }).format('MMMM YYYY');
    const qmb = data.qmb || {};
    const chronic = data.chronicConditions || {};
    const prereqs = det.ccm?.prerequisites || data.ccmPrerequisites || null;
    const ledger = data.ledger;
    const programColor = det.program === 'CCM' ? '#0d6efd' : det.program === 'APCM' ? '#6f42c1' : '#6c757d';

    return (
        <div className="mt-3 p-2 rounded" style={{ border: '1px dashed #ced4da' }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h6 className="mb-0">Program Determination - {monthLabel}</h6>
                <span className="badge" style={{ background: programColor, fontSize: 13 }}>
                    {det.program ? `${det.program}: ${codeStr(det.codes)}${typeof det.expectedAmount === 'number' ? ` · expected ${money(det.expectedAmount)}` : ''}` : 'No billing opportunity this month'}
                </span>
            </div>
            <small className="text-muted d-block">{det.disclaimer}</small>
            {ledger && (
                <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: 12 }}>
                    <b>Minutes by program · grand total {ledger.totalMinutes} min ({ledger.entryCount} physical entries)</b>
                    {Object.entries(ledger.programs).map(([name, item]) => (
                        <div key={name}>{name}: {item.totalMinutes} min total
                            {name === 'RPM' && ` (interactive ${item.interactiveMinutes} · non-interactive ${item.nonInteractiveMinutes})`}
                            {' · '}{item.billableMinutes ?? 0} billable · {item.nonBillableMinutes ?? item.totalMinutes} non-billable — {codeStr(item.codes)}
                            {name === 'RPM' && `; device days ${item.deviceDays} → ${item.deviceCode || 'none'}`}
                            {item.candidateCodes?.length > 0 && ` · PCM alternative ${codeStr(item.candidateCodes)} (requires documented choice)`}
                        </div>
                    ))}
                </div>
            )}
            <div className="mt-1" style={{ fontSize: 13 }}>
                <b>Status:</b>{' '}
                <span style={{ color: det.billableStatus === 'billable-opportunity' ? '#28a745' : det.billableStatus === 'no-opportunity' ? '#6c757d' : '#dc3545', fontWeight: 600 }}>
                    {det.billableStatus}
                </span>
                {' · '}CCM {det.ccm?.qualifies ? `qualifies (${codeStr(det.ccm.codes)}, ${det.ccm.staffMinutes} staff min)` : `not qualifying (${det.ccm?.staffMinutes ?? 0} staff min)`}
                {' · '}APCM {det.apcm?.eligible ? `eligible (${det.apcm.tier.code})` : 'not eligible'}
                {det.program === 'CCM' && det.apcm?.eligible && <span className="text-muted"> - CCM-first: APCM {det.apcm.tier.code} not emitted (never both)</span>}
                {det.apcm?.locked && <span style={{ color: '#dc3545' }}> · APCM locked (already recorded this month)</span>}
                {det.apcm?.excludedBy?.length > 0 && <span style={{ color: '#dc3545' }}> · APCM excluded by {det.apcm.excludedBy.join('/')}</span>}
            </div>
            {det.ccm?.track && (
                <div style={{ fontSize: 12 }}>
                    <b>CCM track:</b> {det.ccm.track} ({det.ccm.staffMinutes ?? 0} staff / {det.ccm.physicianMinutes ?? 0} physician min)
                    {det.ccm.minuteSummary && <span> · {det.ccm.minuteSummary}</span>}
                    {det.ccm.eitherOr && det.ccm.alternative && (
                        <span style={{ color: '#856404' }}> · EITHER/OR: staff and physician CCM codes are bundled - {codeStr(det.ccm.alternative.codes)} ({det.ccm.alternative.track}{typeof det.ccm.alternative.amount === 'number' ? `, ${money(det.ccm.alternative.amount)}` : ''}) is the excluded alternative; higher value selected, biller must choose one.</span>
                    )}
                </div>
            )}
            {prereqs && (
                <div style={{ fontSize: 12 }}>
                    <b>CCM prerequisites (flag only):</b>{' '}
                    <span style={{ color: prereqs.consent ? '#28a745' : '#dc3545' }}>consent {prereqs.consent ? '✓' : 'missing'}</span>{' · '}
                    <span style={{ color: prereqs.enrolled ? '#28a745' : '#dc3545' }}>enrollment {prereqs.enrolled ? '✓' : 'missing'}</span>{' · '}
                    <span style={{ color: prereqs.conditionsMet ? '#28a745' : '#dc3545' }}>{prereqs.conditions ?? '?'} chronic condition(s) {prereqs.conditionsMet ? '✓' : '(<2)'}</span>{' · '}
                    <span style={{ color: prereqs.placeOfService ? '#28a745' : '#dc3545' }}>place of service {prereqs.placeOfService || 'missing'}</span>
                    {canWrite && (
                        <span className="ms-2">
                            <input className="form-control form-control-sm d-inline-block" style={{ width: 60 }} placeholder="POS" value={posInput} onChange={e => setPosInput(e.target.value)} maxLength={2} />
                            <button className="btn btn-outline-primary btn-sm ms-1" onClick={submitPos} disabled={busy || !/^\d{2}$/.test(posInput)}>Set POS</button>
                        </span>
                    )}
                </div>
            )}
            {det.program && (
                <div className="text-muted" style={{ fontSize: 12 }}>
                    Allowed rates used: {(det.codes || []).map(c => `${c.code} ${money(rateScopes[c.code]?.amount)} (${scopeOf(c.code) || 'none'})`).join(' · ')}
                    {det.rates?.region ? ` · region ${det.rates.region}` : ' · no region set on org (national/contract only)'}
                </div>
            )}
            {blocks.map(f => (
                <div key={f.key} className="alert alert-danger py-1 mt-1 mb-1" style={{ fontSize: 13, borderLeft: '4px solid #dc3545' }}>
                    <b>Blocked opportunity:</b> {f.message} <span className="text-muted">(effort capture and reporting continue)</span>
                </div>
            ))}
            {det.optimization?.active && <div className="alert alert-info py-1 mt-1 mb-1" style={{ fontSize: 13 }}>{det.optimization.note}</div>}
            {det.optimization && !det.optimization.active && det.program === 'CCM' && det.apcm?.eligible && (
                <div className="text-muted" style={{ fontSize: 12 }}>Payment comparison: {det.optimization.reason}{typeof det.optimization.ccmAmount === 'number' ? ` (CCM ${money(det.optimization.ccmAmount)} vs APCM ${money(det.optimization.apcmAmount)})` : ''}.</div>
            )}

            {warnings.map(f => (
                <div key={f.key} className="alert alert-warning py-1 mt-1 mb-1" style={{ fontSize: 13, borderLeft: '4px solid #ff9800' }}>
                    <b>Warning:</b> {f.message}
                </div>
            ))}
            {infos.map(f => <div key={f.key} className="text-muted" style={{ fontSize: 12 }}>{f.message}</div>)}
            {data.pcmEnrollmentPrompt?.show && (canWrite
                ? <PcmEnrollPrompt patientId={patientId} prompt={data.pcmEnrollmentPrompt} onEnrolled={async () => { await load(); if (onEnrollmentChanged) await onEnrollmentChanged(); }} />
                : <div className="text-muted" style={{ fontSize: 12 }}>Enroll in PCM? - {data.pcmEnrollmentPrompt.chronicConditionCount} qualifying chronic condition, not enrolled in PCM (enrollment is done by care staff).</div>)}
            {error && <div className="alert alert-danger py-1 mt-1 mb-1">{error}</div>}
            {notice && <div className="alert alert-success py-1 mt-1 mb-1">{notice}</div>}

            <div className="row mt-2">
                <div className="col-md-6">
                    <div className="fw-bold" style={{ fontSize: 13 }}>Initiating visit (prerequisite - flag only)</div>
                    <div style={{ fontSize: 12, background: visit.date ? 'transparent' : '#fff3cd', padding: '2px 4px', borderRadius: 3 }}>
                        {visit.date ? `Last initiating visit ${formatDate(visit.date)}${visit.recordedBy ? ` · by ${visit.recordedBy}` : ''}${visit.note ? ` · ${visit.note}` : ''}` : <span style={{ color: '#856404' }}>No initiating visit on file (new patient, or not seen in 3 years) - not billable pending initiating visit.</span>}
                    </div>
                    {canWrite && (
                        <div className="d-flex gap-1 mt-1">
                            <input type="date" className="form-control form-control-sm" value={visitDate} max={moment().format('YYYY-MM-DD')} onChange={e => setVisitDate(e.target.value)} />
                            <input className="form-control form-control-sm" placeholder="Note (optional)" value={visitNote} onChange={e => setVisitNote(e.target.value)} />
                            <button className="btn btn-outline-primary btn-sm" onClick={submitVisit} disabled={busy || !visitDate}>Save</button>
                        </div>
                    )}
                </div>
                <div className="col-md-6">
                    <div className="fw-bold" style={{ fontSize: 13 }}>Recorded opportunities - {monthLabel} (hand-off to biller, not claims)</div>
                    <div style={{ fontSize: 12 }}>
                        {recorded.length === 0 && <span className="text-muted">None recorded this month.</span>}
                        {recorded.map(r => (
                            <div key={r.id || r._id} className="d-flex justify-content-between">
                                <span><b>{r.program}</b> {codeStr(r.codes)}{typeof r.expectedAmount === 'number' ? ` · ${money(r.expectedAmount)}` : ''}{r.recordedByName ? ` · by ${r.recordedByName}` : ''}{r.status === 'void' ? ' · VOID' : ''}
                                    {(r.noteNotYetLocked || (r.flags || []).some(f => f.code === 'NOTE_NOT_LOCKED')) && <span className="badge bg-info text-dark ms-1" style={{ fontSize: 10 }} title="A supporting clinical note for this month is still open (within its 72h window); informational only.">note not yet locked</span>}
                                </span>
                                {canWrite && r.status !== 'void' && <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={() => voidOpportunity(r.id || r._id)} disabled={busy}>void</button>}
                            </div>
                        ))}
                        <div className="text-muted">One APCM per patient per calendar month across providers/orgs; APCM never shares a month with CCM/PCM/TCM.</div>
                    </div>
                    {canWrite && det.program && !alreadyRecorded(det.program) && (
                        <div className="d-flex gap-1 mt-1">
                            <input className="form-control form-control-sm" placeholder="Note (optional)" value={oppNote} onChange={e => setOppNote(e.target.value)} />
                            <button className="btn btn-outline-success btn-sm text-nowrap" onClick={() => recordOpportunity(det.program)} disabled={busy}>Record {det.program} opportunity</button>
                            {det.optimization?.active && <button className="btn btn-outline-secondary btn-sm text-nowrap" onClick={() => recordOpportunity('APCM')} disabled={busy}>Record APCM instead</button>}
                        </div>
                    )}
                    {canWrite && (
                        <div className="d-flex gap-1 mt-1">
                            {['PCM', 'RPM', 'TCM'].filter(p => !alreadyRecorded(p)).map(p => (
                                <button key={p} className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => recordOpportunity(p)} disabled={busy || (p === 'PCM' && (!det.pcm?.eligible || (det.pcm.conflict && oppNote.trim().length < 10)))}>Record {p} opportunity</button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-12 mb-2" style={{ fontSize: 12 }}>
                    <b>Program enrollment:</b> {(data.enrolledPrograms || []).join(', ') || 'none'}
                    {(data.enrollments || []).filter(e => !e.endedAt).map(e => (
                        <span key={e._id} className="ms-2">{e.program} since {formatDate(e.startedAt)}
                            {e.consentStatus === 'to_verify' && e.consentGiven !== true && !data.consents?.[e.program]?.present && <span className="ms-1" style={{ color: '#856404' }}>(Consent: verify at next contact)</span>}
                            {canWrite && <button className="btn btn-link btn-sm" onClick={() => endEnrollment(e._id)} disabled={busy}>End episode</button>}
                        </span>
                    ))}
                    {canWrite && (
                        <div className="d-flex gap-1 mt-1">
                            <select className="form-select form-select-sm" value={enrollmentProgram} aria-label="Enrollment program" onChange={e => setEnrollmentProgram(e.target.value)}><option value="">Select program</option>{PROGRAMS.map(p => <option key={p}>{p}</option>)}</select>
                            <input type="date" className="form-control form-control-sm" value={enrollmentDate} max={moment().format('YYYY-MM-DD')} onChange={e => setEnrollmentDate(e.target.value)} />
                            <input className="form-control form-control-sm" placeholder="Enrollment note (required)" value={enrollmentNote} onChange={e => setEnrollmentNote(e.target.value)} />
                            <button className="btn btn-outline-primary btn-sm text-nowrap" onClick={submitEnrollment} disabled={busy || !enrollmentProgram || !enrollmentNote.trim()}>Enroll</button>
                        </div>
                    )}
                </div>
                {/* consent per program */}
                <div className="col-md-4">
                    <div className="fw-bold" style={{ fontSize: 13 }}>Consent (per program)</div>
                    {PROGRAMS.map(p => {
                        const c = data.consents?.[p] || {};
                        return (
                            <div key={p} className="d-flex justify-content-between align-items-center" style={{ fontSize: 12, background: c.present ? 'transparent' : '#fff3cd', padding: '2px 4px', borderRadius: 3 }}>
                                <span>
                                    <b>{p}</b>: {c.present ? `on file (${c.method || ''} ${c.consentDate ? formatDate(c.consentDate) : ''}, ${c.source})` : <span style={{ color: '#856404' }}>missing - not billable pending consent</span>}
                                </span>
                                {c.present && c.source !== 'legacy' && canWrite && <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={() => revokeConsent(p)} disabled={busy}>revoke</button>}
                            </div>
                        );
                    })}
                    {canWrite && (
                        <div className="mt-1">
                            <div className="d-flex gap-1">
                                <select className="form-select form-select-sm" value={consentProgram} onChange={e => setConsentProgram(e.target.value)}>{PROGRAMS.map(p => <option key={p}>{p}</option>)}</select>
                                <select className="form-select form-select-sm" value={consentMethod} onChange={e => setConsentMethod(e.target.value)}><option>Written</option><option>Verbal</option></select>
                            </div>
                            <input type="date" className="form-control form-control-sm mt-1" value={consentDate} max={moment().format('YYYY-MM-DD')} onChange={e => setConsentDate(e.target.value)} />
                            <input type="file" className="form-control form-control-sm mt-1" onChange={e => setConsentFile(e.target.files[0] || null)} />
                            <button className="btn btn-outline-primary btn-sm mt-1" onClick={submitConsent} disabled={busy}>Record {consentProgram} consent</button>
                        </div>
                    )}
                </div>

                {/* QMB monthly */}
                <div className="col-md-4">
                    <div className="fw-bold" style={{ fontSize: 13 }}>QMB status - {monthLabel}</div>
                    <div style={{ fontSize: 12, background: qmb.warning ? '#fff3cd' : 'transparent', padding: '2px 4px', borderRadius: 3 }}>
                        {qmb.status === null || qmb.status === undefined ? 'Not recorded (treated as non-QMB → G0557 tier)' : `${qmb.status ? 'QMB' : 'Not QMB'} · ${qmb.verified ? 'verified' : 'UNVERIFIED'}`}
                        {qmb.record?.proof?.source && ` · proof: ${qmb.record.proof.source}${qmb.record.proof.evidenceDate ? ` ${formatDate(qmb.record.proof.evidenceDate)}` : ''}`}
                        {qmb.record?.recordedByName && ` · by ${qmb.record.recordedByName}`}
                        {qmb.warning && <div style={{ color: '#856404' }}>{qmb.warning} - flag only, capture continues.</div>}
                    </div>
                    {canWrite && (
                        <div className="mt-1">
                            <select className="form-select form-select-sm" value={qmbValue} onChange={e => setQmbValue(e.target.value)}>
                                <option value="true">QMB this month</option>
                                <option value="false">Not QMB this month</option>
                            </select>
                            <select className="form-select form-select-sm mt-1" value={qmbSource} onChange={e => setQmbSource(e.target.value)}>
                                <option value="">Proof source…</option>{QMB_SOURCES.map(s => <option key={s}>{s}</option>)}
                            </select>
                            <input type="date" className="form-control form-control-sm mt-1" value={qmbDate} onChange={e => setQmbDate(e.target.value)} />
                            <input type="file" className="form-control form-control-sm mt-1" onChange={e => setQmbFile(e.target.files[0] || null)} />
                            <input className="form-control form-control-sm mt-1" placeholder="Proof note (optional)" value={qmbNote} onChange={e => setQmbNote(e.target.value)} />
                            <div className="d-flex gap-1 mt-1">
                                <button className="btn btn-outline-primary btn-sm" onClick={() => submitQmb(true)} disabled={busy || (!qmbSource && !qmbFile && !qmbNote)}>Save verified (with proof)</button>
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => submitQmb(false)} disabled={busy}>Save unverified</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* chronic conditions */}
                <div className="col-md-4">
                    <div className="fw-bold" style={{ fontSize: 13 }}>Chronic conditions (APCM tier)</div>
                    <div style={{ fontSize: 12, background: chronic.source === 'attested' ? 'transparent' : '#fff3cd', padding: '2px 4px', borderRadius: 3 }}>
                        {chronic.count === null || chronic.count === undefined ? 'Unknown - APCM tier cannot be determined' : `${chronic.count} condition${chronic.count === 1 ? '' : 's'} (${chronic.source})`}
                        {chronic.source === 'estimated' && <div style={{ color: '#856404' }}>Estimated from the diagnosis text - confirm the count.</div>}
                        {chronic.diseases && <div className="text-muted" title={chronic.diseases}>Dx: {chronic.diseases.length > 90 ? `${chronic.diseases.slice(0, 90)}…` : chronic.diseases}</div>}
                        <div className="text-muted">Tiers: 0-1 → G0556 · 2+ → G0557 · 2+ &amp; verified QMB → G0558 (no minute threshold)</div>
                    </div>
                    {canWrite && (
                        <div className="d-flex gap-1 mt-1">
                            <input type="number" min="0" max="50" className="form-control form-control-sm" placeholder="Confirmed count" value={chronicInput} onChange={e => setChronicInput(e.target.value)} />
                            <button className="btn btn-outline-primary btn-sm" onClick={submitChronic} disabled={busy || chronicInput === ''}>Save</button>
                        </div>
                    )}
                </div>
            </div>
            <PlanOfCarePanel patientId={patientId} year={year} month={month} onChanged={load} />
            {canRates && (
                <div className="mt-2">
                    <button className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} onClick={() => setShowRates(v => !v)}>{showRates ? 'Hide' : 'Edit'} allowed amounts (national / region / contract)</button>
                    {showRates && <BillingRatesPanel organizationId={data.organizationId} onChanged={load} />}
                </div>
            )}
        </div>
    );
};

export default ProgramBillingPanel;
